import React from 'react';
import { useTranslation } from 'react-i18next';

import { Contract } from 'cpes-common';
import { Combobox, Layout } from '@vwfs-bronson/bronson-react';
import { useTranslationWithFormatting } from '../../../../localization/useTranslationWithFormatting';
import { getProductCategoryTranslationLabel } from '../../../contracts/ProductCategoryTranslationLabel';
import { LicensePlate } from '../../../license-plate';
import { getListOfAllAvailableContracts } from '../../helper/AvailableContract';
import { ComboboxOption } from '@cp-shared-19/frontend-ui';

export type ContractFilterProps = {
    onChange: (selectedContract: string) => void;
    contracts: Contract[];
    selectedContract?: string;
    allowAll?: boolean;
};

export const ContractFilter: React.FC<ContractFilterProps> = ({
    onChange,
    contracts,
    selectedContract,
    allowAll = true,
}) => {
    const { t: postboxTranslation } = useTranslation('postbox');
    const { t: contractsTranslation } = useTranslationWithFormatting('contracts');

    const availableOptions = getListOfAllAvailableContracts(contracts || []);
    const allTranslation = postboxTranslation('filters.contract.all');

    const availableOptionElements = availableOptions
        .filter(option => !!option.contract || (allowAll && option.key === 'ALL'))
        .map(availableContract => {
            const contract = availableContract.contract;

            if (contract) {
                return {
                    key: contract.contractNumber,
                    value: {
                        textValue: contract.contractNumber as string,
                        content: (
                            <Layout flush>
                                <Layout.Item default="1/1">
                                    <LicensePlate
                                        licensePlateNumber={contract.licensePlateNumber}
                                        size={'xsmall'}
                                        numberOfCars={contract.numberOfCars}
                                        className={'u-text-base'}
                                    />
                                </Layout.Item>
                                <Layout.Item default="1/1">
                                    <strong>{contract.carInformation}</strong>
                                </Layout.Item>
                                <Layout.Item default="1/1">
                                    {contractsTranslation(getProductCategoryTranslationLabel(contract.contractType))}{' '}
                                    {contract.contractNumber}
                                </Layout.Item>
                            </Layout>
                        ),
                    },
                };
            } else {
                return {
                    key: 'ALL',
                    value: {
                        textValue: allTranslation,
                        content: allTranslation,
                    },
                };
            }
        });

    if (!contracts) {
        return null;
    }

    const selectedOption =
        availableOptionElements.find(option => option.key === selectedContract) ||
        availableOptionElements.find(option => allowAll && option.key === 'ALL');
    const selectedValue = selectedOption ? [(selectedOption as unknown) as ComboboxOption] : [];

    const selectValueChange = (selected: { key: string }[]): void => {
        const selectedKey = selected?.[0]?.key;
        if (!selectedKey || selectedKey === selectedOption?.key) {
            return;
        }
        onChange(selectedKey);
    };

    return (
        <>
            <label>{postboxTranslation('filters.contract.label')}</label>
            <Combobox
                testId={'ContractFilter'}
                onChange={selectValueChange}
                value={selectedValue}
                placeholder={postboxTranslation('filters.contract.placeholder')}
                options={availableOptionElements}
                className="u-mb-large c-form-field--no-floating"
            />
        </>
    );
};
